import classNames from 'classnames';
import { I18nKey } from 'i18n/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import React, { useCallback, useMemo } from 'react';
import Select from 'react-select';
import { ReviewsOrderBy } from 'api/resort/types';
import styles from './styles.module.scss';

export const SortByReviewsMap: Record<ReviewsOrderBy, I18nKey['common']> = {
  [ReviewsOrderBy.date]: 'resort.reviews.mostRecent',
  [ReviewsOrderBy.highestRating]: 'resort.reviews.highestRating',
  [ReviewsOrderBy.lowestRating]: 'resort.reviews.lowestRating',
};

export const SortByReviewsOptions = [ReviewsOrderBy.date, ReviewsOrderBy.highestRating, ReviewsOrderBy.lowestRating].map(
  (e) => ({
    value: e,
    label: SortByReviewsMap[e],
  }),
);

export interface IProps {
  sortBy: ReviewsOrderBy,
  setSortBy: (v: ReviewsOrderBy) => void,
  className?: string,
}

export const SortReviewsRating = (
  { setSortBy, sortBy, className }: IProps,
) => {
  const { t } = useOTSTranslation('common');

  const sortByVal = useMemo(() => ({
    label: t(SortByReviewsMap[sortBy]),
    value: sortBy,
  }), [sortBy]);

  const handleChangeSortReviews = useCallback((value: {
    label: string;
    value: ReviewsOrderBy;
  } | null) => {
    const arr = Array.isArray(value) ? value : [value];
    const n = arr[0];
    setSortBy(n.value);
  }, [setSortBy]);

  const options = useMemo(() => SortByReviewsOptions.map((v) => ({
    value: v.value,
    label: t(v.label),
  })), []);

  const customStyles = {
    option: () => ({
      padding: '5px 13px',
      background: '#FFFFFF',
      fontSize: '14px',
      color: '#576A85',
      cursor: 'pointer',
      margin: '5px 0',
    }),
    menuList: () => ({
      width: 140,
    }),
    indicatorsContainer: () => ({
      ' > div': {
        padding: 0,
      },
      border: 'none',
      padding: 0,
    }),
    valueContainer: () => ({
      fontSize: '14px',
      lineHeight: '32px',
      color: '#576A85',
    }),
    // eslint-disable-next-line @typescript-eslint/no-shadow
    singleValue: (styles: { [key: string]: string }) => ({
      ...styles,
      color: '#576A85',
      fontSize: '14px',
      lineHeight: '32px',
      marginRight: '5px',
      maxWidth: 'calc(100% - 45px)',
    }),
    control: () => ({
      minWidth: 160,
      background: '#FFFFFF',
      border: '1px solid rgba(12, 113, 176, 0.2)',
      borderRadius: '5px',
      display: 'flex',
      justifyContent: 'space-between',
      padding: '5px 10px 5px 10px',
      cursor: 'pointer',
    }),
  };

  return (
    <section className={classNames(styles.box, className)}>
      <h3>{t('resort.reviews.allReviews')}</h3>
      <div>
        <label htmlFor="resort.reviews.sortBy">{t('resort.reviews.orderBy')}</label>
        <Select
          id="resort.reviews.sortBy"
          isSearchable={false}
          onChange={handleChangeSortReviews}
          options={options}
          value={sortByVal}
          styles={customStyles}
        />
      </div>
    </section>
  );
};
